import { ReferenceImage } from "@/types";
import { create } from "zustand";
import { VideoProjectSchema } from "../components/VideoProject/schema";
import { VideoInputSchema } from "../components/VideoInputs/schema";
import { VideoGenerationDataProps } from "../types";
import { VideoGenerationStatus } from "../../saved-projects/types";

const defaultState: Omit<
  VideoGenerationDataProps,
  | "updateVideoField"
  | "updateVideoConfig"
  | "updateVideoInput"
  | "updateVideoReview"
  | "resetAll"
  | "setLanguage"
  | "hasVideoProjectFormChanged"
  | "handleFormChange"
  | "setIsFormChanged"
> = {
  projectName: "",
  projectId: "",
  resolution: "720",
  duration: 8,
  aspectRatio: "16:9",
  isFormChanged: false,

  videoInputs: {
    description: "",
    firstFrameImage: {
      url: "",
      fileId: "",
      fileName: "",
      base64: "",
    } as ReferenceImage,
    lastFrameImage: {
      url: "",
      fileId: "",
      fileName: "",
      base64: "",
    } as ReferenceImage,
    cachedRaiResponse: {
      videoDescription: null,
      firstFrameUrl: null,
      lastFrameUrl: null,
    },
    cachedRaiError: {
      firstFrameUrl: "",
      lastFrameUrl: "",
      videoDescription: "",
    },
    cachedRaiSuccess: {
      videoDescription: false,
      firstFrameUrl: false,
      lastFrameUrl: false,
    },
    isFrameChecked: false,
  },

  videoReview: {
    videoUrl: "",
    videoRaiStatus: null,
    language: "",
    modelOutputs: {
      videoTaskId: "",
      type: "",
    },
    videoFileName: "",
    cachedOutputRaiResponse: null,
    raiFailedCompliances: "",
    version: 0,
    generationStatus: VideoGenerationStatus.NOT_STARTED,
    transcript: "",
    isTranscriptFailed: false,
    transcriptRAIFailedCompliances: "",
    transcriptOutputRAIResponse: null,
    transcriptRAIStatus: null,
  },
};

export const useVideoGenerationStore = create<VideoGenerationDataProps>()((set, get) => ({
  ...defaultState,

  setLanguage: (language: string) =>
    set((state) => ({
      ...state,
      videoReview: {
        ...state.videoReview,
        language,
      },
    })),

  updateVideoField: (key, value) =>
    set((state) => ({
      ...state,
      [key]: value,
    })),

  updateVideoConfig: (data) =>
    set((state) => ({
      ...state,
      ...data,
    })),

  updateVideoInput: (data: Partial<VideoGenerationDataProps["videoInputs"]>) =>
    set((state) => ({
      videoInputs: {
        ...state.videoInputs,
        ...data,
      },
    })),

  updateVideoReview: (data: Partial<VideoGenerationDataProps["videoReview"]>) =>
    set((state) => ({
      videoReview: {
        ...state.videoReview,
        ...data,
      },
    })),

  setIsFormChanged: (value: boolean) =>
    set((state) => ({
      ...state,
      isFormChanged: value,
    })),

  handleFormChange: (data: VideoProjectSchema | VideoInputSchema, step: number) => {
    const prev = get();
    let hasChanged;

    switch (step) {
      case 1:
        if ("projectName" in data && "resolution" in data && "aspectRatio" in data && "duration" in data) {
          hasChanged =
            prev.projectName !== data.projectName ||
            prev.resolution !== data.resolution?.label ||
            prev.aspectRatio !== data.aspectRatio?.label ||
            prev.duration !== Number(data.duration?.id ?? 0);
        }
        break;
      case 2:
        if ("videoDescription" in data) {
          hasChanged =
            prev.videoInputs.description !== data.videoDescription ||
            (prev.videoInputs.firstFrameImage?.url ?? "") !== (data.firstFrameUrl?.url ?? "") ||
            (prev.videoInputs.lastFrameImage?.url ?? "") !== (data.lastFrameUrl?.url ?? "");
        }
        break;
      default:
        hasChanged = false;
    }

    return hasChanged ?? false;
  },

  resetAll: () => set(defaultState),
}));
